import type { Bundle, Plan } from "@memorang/types";
type PlanRule = {
	interval: string;
	duration: number;
	minDays?: number;
	maxDays?: number;
	scoreCalculator: (daysLeft: number) => number;
};

// Interval order for sorting plans
const INTERVAL_ORDER = { day: 1, month: 2, year: 3 } as const;

// Plan eligibility and scoring rules based on exam timeframe
const PLAN_RULES: PlanRule[] = [
	{
		interval: "day",
		duration: 7,
		maxDays: 30,
		scoreCalculator: (daysLeft) => (daysLeft <= 14 ? 100 : 50),
	},
	{
		interval: "month",
		duration: 1,
		minDays: 7,
		maxDays: 90,
		scoreCalculator: (daysLeft) => (daysLeft >= 7 && daysLeft <= 45 ? 90 : 40),
	},
	{
		interval: "month",
		duration: 3,
		minDays: 35,
		maxDays: 180,
		scoreCalculator: (daysLeft) =>
			daysLeft >= 45 && daysLeft <= 120 ? 80 : 30,
	},
	{
		interval: "month",
		duration: 6,
		minDays: 90,
		maxDays: 270,
		scoreCalculator: (daysLeft) =>
			daysLeft >= 120 && daysLeft <= 210 ? 70 : 20,
	},
	{
		interval: "month",
		duration: 12,
		minDays: 180,
		scoreCalculator: (daysLeft) => (daysLeft >= 180 ? 60 : 10),
	},
];

/**
 * Checks if a plan is eligible based on days left until exam
 */
const isPlanEligible = (plan: Plan, daysLeft: number): boolean => {
	const rule = PLAN_RULES.find(
		(r) => r.interval === plan.interval && r.duration === plan.duration,
	);

	if (!rule) {
		return false;
	}

	const isAfterMinDate = rule.minDays === undefined || daysLeft >= rule.minDays;
	const isBeforeMaxDate =
		rule.maxDays === undefined || daysLeft <= rule.maxDays;

	return isAfterMinDate && isBeforeMaxDate;
};

/**
 * Calculate a score for how well a plan matches the exam timeframe
 */
const calculatePlanScore = (plan: Plan, daysLeft: number): number => {
	const rule = PLAN_RULES.find(
		(r) => r.interval === plan.interval && r.duration === plan.duration,
	);

	return rule?.scoreCalculator(daysLeft) || 0;
};

/**
 * Sort plans by remaining plans based on exam timeframe
 */
const sortRemainingPlans = (a: Plan, b: Plan, daysLeft: number): number => {
	// For short exams, prioritize shorter plans
	if (daysLeft <= 30) {
		if (a.interval !== b.interval) {
			return a.interval === "day" ? -1 : 1;
		}
		return a.duration - b.duration;
	}

	// For medium exams, prioritize monthly plans
	if (daysLeft <= 120) {
		if (a.interval !== b.interval) {
			return a.interval === "month" ? -1 : 1;
		}
		return a.duration - b.duration;
	}

	// For long exams, prioritize longer plans
	if (a.interval !== b.interval) {
		return (
			INTERVAL_ORDER[b.interval as keyof typeof INTERVAL_ORDER] -
			INTERVAL_ORDER[a.interval as keyof typeof INTERVAL_ORDER]
		);
	}

	return b.duration - a.duration;
};

/**
 * Sort plans by interval and duration in ascending order
 */
const sortPlansByIntervalAndDuration = (a: Plan, b: Plan): number => {
	const aOrder = INTERVAL_ORDER[a.interval as keyof typeof INTERVAL_ORDER];
	const bOrder = INTERVAL_ORDER[b.interval as keyof typeof INTERVAL_ORDER];

	if (aOrder !== bOrder) {
		return aOrder - bOrder;
	}

	// If same interval, sort by duration (ascending)
	return a.duration - b.duration;
};

/**
 * Filter and prioritize plans based on exam date
 * @param plans List of available plans
 * @param daysLeft Number of days until exam
 * @returns Up to 3 prioritized plans sorted by interval and duration
 */
export const filterPlansByExamDate = (
	plans: Plan[],
	daysLeft?: number,
): Plan[] => {
	// If no exam date is provided, return top 3 plans
	if (!daysLeft) {
		return plans.slice(0, 3).sort(sortPlansByIntervalAndDuration);
	}

	// Filter plans based on exam date timeframes
	const filteredPlans = plans.filter((p) => isPlanEligible(p, daysLeft));
	let resultPlans: Plan[] = [];

	if (filteredPlans.length > 3) {
		// Sort plans by how well they match the exam timeframe
		const scoredPlans = filteredPlans
			.map((plan) => ({
				plan,
				score: calculatePlanScore(plan, daysLeft),
			}))
			.sort((a, b) => b.score - a.score);

		resultPlans = scoredPlans.slice(0, 3).map((sp) => sp.plan);
	} else if (filteredPlans.length < 3) {
		// Add appropriate plans from the original list until we reach 3
		const remainingPlans = plans
			.filter(
				(p) =>
					!filteredPlans.some(
						(fp) => fp.interval === p.interval && fp.duration === p.duration,
					),
			)
			.sort((a, b) => sortRemainingPlans(a, b, daysLeft));

		resultPlans = [...filteredPlans, ...remainingPlans].slice(0, 3);
	} else {
		resultPlans = filteredPlans;
	}

	// Sort the final plans by interval and duration in ascending order
	return resultPlans.sort(sortPlansByIntervalAndDuration);
};

export const hasPurchaseBundleData = (
	productDetails?: Bundle | null,
): productDetails is Bundle => {
	if (!productDetails) {
		return false;
	}

	return (
		typeof productDetails.logicalId === "string" &&
		productDetails.logicalId.length > 0 &&
		typeof productDetails.numericId === "number"
	);
};
